import React from 'react';
import cx from 'classnames';
import { DayModifiers, RangeModifier } from 'react-day-picker';
import { makeStyles } from '@material-ui/core/styles';

import { IconButton, Tooltip, Typography, units } from '@ha/dsl';

import isDayInMoveInWindow from '../helpers/isDayInMoveInWindow';
import makeDayTestLocator from '../helpers/makeDayTestLocator';
import areModifiersEqual from '../helpers/areModifiersEqual';
import { DAY_COLUMN_SIZE_DESKTOP, DAY_COLUMN_SIZE_MOBILE } from '../constants';

const useStyles = makeStyles(theme => ({
  dayButton: {
    width: DAY_COLUMN_SIZE_DESKTOP,
    height: DAY_COLUMN_SIZE_DESKTOP,
    padding: 0,

    [theme.breakpoints.down('sm')]: {
      width: DAY_COLUMN_SIZE_MOBILE,
      height: DAY_COLUMN_SIZE_MOBILE,
    },
  },
  moveInWindow: {
    border: units.border(1, 'solid', theme.palette.secondary.dark),
  },
  outsideMoveInWindow: {
    cursor: 'not-allowed',
  },
}));

interface OwnProps {
  day: Date;
  highlightMoveInWindows: boolean;
  modifiers?: DayModifiers;
  moveInWindows?: RangeModifier[];
  moveInWindowsTooltip: string;
  customTooltip?: React.ElementType<{ day: Date }>;
}

interface DayWithTooltipProps {
  children: React.ReactElement;
  moveInWindowsTooltip: string;
  showTooltip: boolean;
}

const DayWithTooltip: React.FC<DayWithTooltipProps> = ({
  children,
  moveInWindowsTooltip,
  showTooltip,
}) => {
  if (!showTooltip) {
    return children;
  }

  return (
    <Tooltip
      data-test-locator="Calendar / Day / Move-in tooltip"
      placement="top"
      title={moveInWindowsTooltip}
      leaveTouchDelay={4000}
    >
      {children}
    </Tooltip>
  );
};

const Day: React.FC<OwnProps> = props => {
  const {
    day,
    highlightMoveInWindows,
    modifiers,
    moveInWindows = [],
    moveInWindowsTooltip,
    customTooltip: CustomTooltip,
  } = props;
  const { dayButton, moveInWindow, outsideMoveInWindow } = useStyles();

  const isInMoveInWindow = isDayInMoveInWindow(moveInWindows, day);

  const highlightMoveInWindow = React.useCallback(() => {
    if (!modifiers) {
      return false;
    }

    if (!highlightMoveInWindows) {
      return false;
    }

    const { start, end, disabled } = modifiers;

    if (start || end || disabled) {
      return false;
    }

    return isInMoveInWindow;
  }, [highlightMoveInWindows, isInMoveInWindow, modifiers]);

  const makeDayButtonTestLocator = React.useCallback(() => {
    const defaultLocator = `Calendar / Day / ${makeDayTestLocator(day)}`;

    if (isInMoveInWindow && highlightMoveInWindows) {
      return `${defaultLocator} / MoveIn`;
    }

    if (!modifiers) {
      return defaultLocator;
    }

    const { disabled, end, start } = modifiers;

    if (disabled) {
      return `${defaultLocator} / Disabled`;
    }

    if (end) {
      return `${defaultLocator} / End`;
    }

    if (start) {
      return `${defaultLocator} / Start`;
    }

    return defaultLocator;
  }, [day, highlightMoveInWindows, isInMoveInWindow, modifiers]);

  const shouldApplyMoveInWindows =
    highlightMoveInWindows && moveInWindows.length > 0;

  const content = (
    <IconButton
      className={cx(dayButton, {
        [moveInWindow]: highlightMoveInWindow(),
        [outsideMoveInWindow]: shouldApplyMoveInWindows && !isInMoveInWindow,
      })}
      color="inherit"
      data-test-locator={makeDayButtonTestLocator()}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (shouldApplyMoveInWindows && !isInMoveInWindow) {
          e.stopPropagation();
        }
      }}
    >
      <Typography.Paragraph
        color="inherit"
        data-test-locator={makeDayTestLocator(day)}
      >
        {day.getDate()}
      </Typography.Paragraph>
    </IconButton>
  );

  return CustomTooltip && modifiers?.booked ? (
    <CustomTooltip day={day}>{content}</CustomTooltip>
  ) : (
    <DayWithTooltip
      moveInWindowsTooltip={moveInWindowsTooltip}
      showTooltip={shouldApplyMoveInWindows && !isInMoveInWindow}
    >
      {content}
    </DayWithTooltip>
  );
};

function areEqual(prevProps: OwnProps, nextProps: OwnProps) {
  // re-render a Day only if modifiers have been changed: day became disabled/selected etc.
  const { modifiers: prevModifiers = {} } = prevProps;
  const { modifiers: nextModifiers = {} } = nextProps;

  return (
    areModifiersEqual(prevModifiers, nextModifiers) &&
    prevProps.highlightMoveInWindows === nextProps.highlightMoveInWindows
  );
}

export default React.memo(Day, areEqual);
